(() => {
  const tagName = 'agency-awards-highlights';
  if (!window.customElements || customElements.get(tagName)) return;

  class AgencyAwardsHighlights extends HTMLElement {
    connectedCallback() {
      if (this._connected) return;
      this._connected = true;
      const connection = (this._connection || 0) + 1;
      this._connection = connection;
      queueMicrotask(() => {
        if (this._connected && this._connection === connection) this._setup(connection);
      });
    }

    _setup(connection) {
      this._preview = this.querySelector('[data-awards-preview]');
      this._groups = this.querySelector('[data-awards-groups]');
      if (!this._preview || !this._groups) return;
      this._awards = Array.from(this.querySelectorAll('[data-award]'));
      this._expanded = new Map(this._awards.map((award) => [award, award.open]));
      this._names = new Map(this._awards.map((award) => [award, award.getAttribute('name')]));
      this._panelInert = new Map(this._awards.map((award) => [award, award.querySelector('.agency-awards-highlights__panel')?.hasAttribute('inert')]));
      this._motions = new Map();
      this._motionPreference = window.matchMedia('(prefers-reduced-motion: reduce)');
      this._awards.forEach((award) => award.removeAttribute('name'));
      this._heading = this.querySelector('.agency-awards-highlights__heading');
      this._displayWrap = this.querySelector('.agency-awards-highlights__display-wrap');
      this._controls = Array.from(this._groups.querySelectorAll('a, button, input, select, textarea, [tabindex]'));
      this._inertStates = new Map([...this._awards, ...this._controls].map((element) => [element, element.hasAttribute('inert')]));
      this._cta = this.querySelector('.agency-awards-highlights__cta');
      this._editorExpandedInitially = this.hasAttribute('data-editor-expanded');
      this._editor = this._editorExpandedInitially || Boolean(window.Shopify && window.Shopify.designMode);
      if (this._editor) this.setAttribute('data-editor-expanded', '');
      this._abort = new AbortController();
      const options = { signal: this._abort.signal };
      this.addEventListener('click', (event) => {
        const summary = event.target.closest('.agency-awards-highlights__summary');
        const award = summary?.parentElement;
        if (!this._expanded.has(award) || event.target.closest('a, button, input, select, textarea')) return;
        event.preventDefault();
        this._setExpanded(award, !this._expanded.get(award));
      }, options);
      this.addEventListener('toggle', (event) => this._toggle(event), { ...options, capture: true });
      this.addEventListener('focusin', () => this._schedule(), options);
      this.addEventListener('load', (event) => {
        const award = event.target.closest('[data-award]');
        if (this._motions.has(award)) this._animateAward(award, this._expanded.get(award), true);
        this._schedule();
      }, { ...options, capture: true });
      window.addEventListener('resize', () => { this._settleMotions(); this._schedule(); }, options);
      this._motionPreference.addEventListener('change', () => { this._settleMotions(); this._schedule(); }, options);
      document.addEventListener('shopify:block:select', (event) => {
        if (!this._editor || !this.contains(event.target)) return;
        const award = event.target.closest('[data-award]');
        if (!award || !this.contains(award)) return;
        this._settleMotions();
        this._setExpanded(award, true, false);
        this._schedule();
      }, options);
      if ('ResizeObserver' in window) {
        this._resizeObserver = new ResizeObserver(() => this._schedule());
        this._resizeObserver.observe(this._groups);
        if (this._displayWrap) this._resizeObserver.observe(this._displayWrap);
        this._awards.forEach((award) => this._resizeObserver.observe(award));
      }
      if (this._heading) {
        this._headingObserver = new MutationObserver(() => this._schedule());
        this._headingObserver.observe(this._heading, { childList: true, characterData: true, subtree: true });
      }
      if (document.fonts) {
        document.fonts.addEventListener('loadingdone', () => { this._settleMotions(); this._schedule(); }, options);
      }
      this.setAttribute('data-awards-ready', '');
      this._schedule();
    }

    _toggle(event) {
      const award = event.target;
      if (!(award instanceof HTMLDetailsElement) || !award.matches('[data-award]') || !this.contains(award)) return;
      if (!this._motions.has(award) && this._expanded.get(award) !== award.open) this._setExpanded(award, award.open, false);
      this._schedule();
    }

    _setExpanded(award, expanded, animate = true) {
      if (expanded) this._awards.forEach((other) => {
        if (other !== award && this._expanded.get(other)) this._animateAward(other, false, animate);
      });
      this._animateAward(award, expanded, animate);
    }

    _animateAward(award, expanded, animate) {
      const startHeight = award.getBoundingClientRect().height;
      const previous = this._motions.get(award);
      if (previous) {
        previous.onfinish = null;
        previous.cancel();
        this._motions.delete(award);
      }
      this._expanded.set(award, expanded);
      const panel = award.querySelector('.agency-awards-highlights__panel');
      const summary = award.querySelector('summary');
      panel?.toggleAttribute('inert', !expanded || this._panelInert.get(award) === true);
      if (!animate || this._editor || this._motionPreference.matches || !this.hasAttribute('data-motion-enabled') || !award.animate) {
        this._finishAward(award);
        return;
      }
      award.style.removeProperty('height');
      award.removeAttribute('data-award-animating');
      award.open = true;
      award.toggleAttribute('data-award-closing', !expanded);
      const summaryStyle = getComputedStyle(summary);
      const awardStyle = getComputedStyle(award);
      const rowPadding = parseFloat(getComputedStyle(this).getPropertyValue('--awards-row-padding')) || 16;
      const openBottomPadding = window.matchMedia('(min-width: 750px)').matches ? 12 : rowPadding;
      const border = parseFloat(awardStyle.borderTopWidth) + parseFloat(awardStyle.borderBottomWidth);
      const summaryHeight = summary.getBoundingClientRect().height;
      const currentBottomPadding = parseFloat(summaryStyle.paddingBottom);
      const expandedContentHeight = (panel || summary).getBoundingClientRect().bottom - award.getBoundingClientRect().top + parseFloat(awardStyle.borderBottomWidth) + openBottomPadding - currentBottomPadding;
      const minimumHeight = parseFloat(awardStyle.minHeight) || 0;
      const endHeight = expanded ? Math.max(expandedContentHeight, minimumHeight) : summaryHeight + rowPadding - currentBottomPadding + border;
      award.setAttribute('data-award-animating', '');
      award.style.height = `${startHeight}px`;
      const motion = award.animate([{ height: `${startHeight}px` }, { height: `${endHeight}px` }], {
        duration: 720,
        easing: 'cubic-bezier(0.4, 0, 0.6, 1)',
        fill: 'forwards'
      });
      this._motions.set(award, motion);
      motion.onfinish = () => {
        if (this._motions.get(award) !== motion) return;
        this._motions.delete(award);
        motion.onfinish = null;
        this._finishAward(award);
        motion.cancel();
      };
      this._schedule();
    }

    _finishAward(award) {
      award.open = this._expanded.get(award);
      award.removeAttribute('data-award-closing');
      award.removeAttribute('data-award-animating');
      award.style.removeProperty('height');
      const panel = award.querySelector('.agency-awards-highlights__panel');
      panel?.toggleAttribute('inert', this._panelInert.get(award) === true);
      this._schedule();
    }

    _settleMotions() {
      this._motions?.forEach((motion, award) => {
        motion.onfinish = null;
        motion.cancel();
        this._finishAward(award);
      });
      this._motions?.clear();
    }

    _schedule() {
      if (!this._connected || this._frame || !this._groups) return;
      this._frame = requestAnimationFrame(() => {
        this._frame = 0;
        if (this._connected) this._update();
      });
    }

    _setInert(element, inert) {
      const nextInert = inert || this._inertStates.get(element) === true;
      if (element.hasAttribute('inert') !== nextInert) element.toggleAttribute('inert', nextInert);
    }

    _measureHeadingSize() {
      if (!this._heading || !this._displayWrap) return;
      const wrapStyle = getComputedStyle(this._displayWrap);
      const availableWidth = this._displayWrap.clientWidth - parseFloat(wrapStyle.paddingLeft) - parseFloat(wrapStyle.paddingRight);
      const textWidth = this._heading.getBoundingClientRect().width;
      const currentSize = parseFloat(getComputedStyle(this._heading).fontSize);
      if (availableWidth <= 0 || textWidth <= 0 || !currentSize) return;
      const nextSize = Math.floor(currentSize * (availableWidth - 0.5) / textWidth * 100) / 100;
      if (nextSize > 0 && Math.abs(nextSize - currentSize) > 0.02) {
        return `${nextSize}px`;
      }
    }

    _update() {
      if (!this._preview.clientWidth) return;
      // Read the complete layout before changing heading size, clipping or inert state.
      const headingSize = this._measureHeadingSize();
      const styles = getComputedStyle(this);
      const smallScreen = window.matchMedia('(max-width: 749px)').matches;
      const baseHeight = parseFloat(styles.getPropertyValue(smallScreen ? '--awards-preview-mobile' : '--awards-preview-desktop')) || (smallScreen ? 720 : 800);
      const fadeHeight = parseFloat(styles.getPropertyValue('--awards-fade-height')) || 180;
      const top = this._groups.getBoundingClientRect().top;
      const groupHeight = this._groups.offsetHeight;
      const rects = new Map();
      const measure = (element) => {
        if (!rects.has(element)) rects.set(element, element.getBoundingClientRect());
        return rects.get(element);
      };
      const awardSummaries = this._awards.map((award) => ({ award, summary: award.querySelector('summary') }));
      const summaryRects = awardSummaries.filter(({ summary }) => summary).map(({ award, summary }) => ({ award, rect: measure(summary) }));
      const controlRects = this._controls.map((control) => ({ control, rect: measure(control) }));
      let previewHeight = baseHeight;
      const expanded = this._awards.find((award) => this._expanded.get(award));
      if (expanded) previewHeight = Math.max(previewHeight, measure(expanded).bottom - top + fadeHeight + 16);
      const focused = document.activeElement;
      const focusedAward = focused?.closest('[data-award]');
      if (focusedAward && this._groups.contains(focusedAward)) {
        const summary = focusedAward.querySelector('summary');
        if (summary) previewHeight = Math.max(previewHeight, measure(summary).bottom - top + fadeHeight + 16);
      }
      const hasOverflow = !this._editor && !this.hasAttribute('data-show-all-awards') && this.hasAttribute('data-preview-enabled') && groupHeight > previewHeight + 2;
      const safeBottom = hasOverflow ? top + previewHeight - fadeHeight : Infinity;
      const pageCanvas = this._findCanvas();

      // Heading fitting only translates the groups; their relative row positions stay valid.
      if (headingSize && this._heading.style.getPropertyValue('--awards-heading-size') !== headingSize) {
        this._heading.style.setProperty('--awards-heading-size', headingSize);
      }
      if (this.hasAttribute('data-has-overflow') !== hasOverflow) this.toggleAttribute('data-has-overflow', hasOverflow);
      const heightValue = `${Math.ceil(previewHeight)}px`;
      if (this.style.getPropertyValue('--awards-current-height') !== heightValue) this.style.setProperty('--awards-current-height', heightValue);
      if (pageCanvas && this.style.getPropertyValue('--awards-canvas') !== pageCanvas) this.style.setProperty('--awards-canvas', pageCanvas);
      summaryRects.forEach(({ award, rect }) => {
        this._setInert(award, hasOverflow && rect.bottom > safeBottom);
      });
      controlRects.forEach(({ control, rect }) => {
        this._setInert(control, hasOverflow && rect.height > 0 && rect.bottom > safeBottom);
      });
      if (focused && this._groups.contains(focused) && focused.closest('[inert]')) {
        const summary = this._awards.find((award) => !award.hasAttribute('inert'))?.querySelector('summary');
        (this._cta || summary)?.focus({ preventScroll: true });
      }
    }

    _findCanvas() {
      if (this.classList.contains('agency-awards-highlights--white')) return '#ffffff';
      if (this.classList.contains('agency-awards-highlights--off_white')) return '#fafafa';
      let element = this.parentElement;
      while (element) {
        const color = getComputedStyle(element).backgroundColor;
        if (color && color !== 'rgba(0, 0, 0, 0)' && color !== 'transparent') return color;
        element = element.parentElement;
      }
      return '#fafafa';
    }

    disconnectedCallback() {
      this._connected = false;
      this._abort?.abort();
      this._settleMotions();
      this._names?.forEach((name, award) => { if (name !== null) award.setAttribute('name', name); });
      this._resizeObserver?.disconnect();
      this._headingObserver?.disconnect();
      this._heading?.style.removeProperty('--awards-heading-size');
      if (this._frame) cancelAnimationFrame(this._frame);
      this._frame = 0;
      this._inertStates?.forEach((inert, element) => element.toggleAttribute('inert', inert));
      if (!this._editorExpandedInitially) this.removeAttribute('data-editor-expanded');
      this.removeAttribute('data-awards-ready');
      this.removeAttribute('data-has-overflow');
      this.style.removeProperty('--awards-current-height');
      this.style.removeProperty('--awards-canvas');
    }
  }

  customElements.define(tagName, AgencyAwardsHighlights);
})();
